import { z } from 'zod'

import { dateGteIso, dateLtIso } from './date-utils'
import { getHostRuntime } from '@/Atomic/Widgets/AiChatBot/core/engine/host-runtime'
import { defineTool } from '@/Atomic/Widgets/AiChatBot/core/engine/runtime/tool-system'
import type { SessionContext } from '@/Atomic/Widgets/AiChatBot/core/engine/types'
import { type ScrivitoAccessor } from '@/Atomic/Widgets/AiChatBot/core/tools/shared/entity-serializer'
import { DataClassName } from '@/Data/const'
import { ActivityType } from '@/Widgets/TimeTrackingOverviewWidget/const'

export const fetchTeamUtilizationTool = defineTool({
  name: 'fetch_team_utilization',
  description:
    'Fetch team utilization data for one or more leaders within a date range. Returns per-member breakdown: name, tracked hours, billable hours, non-billable hours, working hours, utilization %, absent days, not-tracked hours, support hours, managed-team hours. Also returns team totals.',
  inputSchema: () =>
    z.object({
      leaderIds: z
        .array(z.string())
        .min(1)
        .describe('One or more leader user IDs to include'),
      startDate: z.string().describe('Range start in YYYY-MM-DD format'),
      endDate: z.string().describe('Range end in YYYY-MM-DD format'),
    }),
  concurrencySafe: true,

  async run(
    args: { leaderIds: string[]; startDate: string; endDate: string },
    _ctx: SessionContext,
  ) {
    const { leaderIds, startDate, endDate } = args
    const { data } = getHostRuntime()

    if (
      !data.resolveDataClass(DataClassName.TIMERECORD_TEAM) ||
      !data.resolveDataClass(DataClassName.TIMERECORD) ||
      !data.resolveDataClass(DataClassName.TIMERECORD_UTILIZATION) ||
      !data.resolveDataClass(DataClassName.TIMERECORD_USER)
    ) {
      throw new Error('Required TimeRecord DataClasses not found')
    }

    // 1. For each leader, get team members
    const allMemberIds = new Set<string>()
    const leaderData = await Promise.all(
      leaderIds.map(async (leaderId) => {
        const teamRecords = (await data.query(DataClassName.TIMERECORD_TEAM, {
          filters: { leader: leaderId },
        })) as ScrivitoAccessor[]
        const memberIds = teamRecords
          .map((item) => item.get('member') as string)
          .filter(Boolean)
        allMemberIds.add(leaderId)
        memberIds.forEach((id) => allMemberIds.add(id))

        const leaderItem = (await data.get(
          DataClassName.TIMERECORD_USER,
          leaderId,
        )) as ScrivitoAccessor | null
        return {
          leaderId,
          name: (leaderItem?.get('name') as string) ?? 'Unknown',
          memberCount: memberIds.length,
        }
      }),
    )

    // 2. For each member, fetch Timerecord + TimerecordUtilization
    const dateFilters = {
      'recordedAt.gte': dateGteIso(startDate),
      'recordedAt.lt': dateLtIso(endDate),
    }
    const utilizationDateFilters = {
      'date.gte': dateGteIso(startDate),
      'date.lt': dateLtIso(endDate),
    }

    const memberData = await Promise.all(
      Array.from(allMemberIds).map(async (userId) => {
        const [timerecords, utilizations, userItem] = await Promise.all([
          data.query(DataClassName.TIMERECORD, {
            filters: { user: userId, ...dateFilters },
            order: [['recordedAt', 'desc']],
            unlimited: true,
          }) as Promise<ScrivitoAccessor[]>,
          data.query(DataClassName.TIMERECORD_UTILIZATION, {
            filters: { user: userId, ...utilizationDateFilters },
            unlimited: true,
          }) as Promise<ScrivitoAccessor[]>,
          data.get(
            DataClassName.TIMERECORD_USER,
            userId,
          ) as Promise<ScrivitoAccessor | null>,
        ])

        const name = (userItem?.get('name') as string) ?? 'Unknown'

        // Aggregate utilization: working time, utilization %, absent days
        let totalWorkingTime = 0
        let totalUtilizationWeighted = 0
        let absentDays = 0

        for (const record of utilizations) {
          const workingTime = Number(record.get('workingTime')) || 0
          const utilizationTime = Number(record.get('utilizationTime')) || 0
          // Mirrors accumulateUtilization.ts in the UI table exactly.
          const utilizationFactor = Number(record.get('utilizationFactor')) || 0
          const isAbsent = record.get('isAbsent') as boolean

          if (isAbsent) {
            absentDays += 1
            continue
          }

          totalWorkingTime += workingTime
          totalUtilizationWeighted += utilizationTime * utilizationFactor
        }

        const utilizationPct =
          totalWorkingTime > 0
            ? Math.round((totalUtilizationWeighted * 100) / totalWorkingTime)
            : 0

        // Aggregate hours from timerecords
        let totalHours = 0
        let billableHours = 0
        let nonBillableHours = 0
        let supportHours = 0
        let managedTeamHours = 0

        for (const record of timerecords) {
          const totalDuration = (record.get('totalDuration') as number) ?? 0
          const billedDuration = (record.get('billedDuration') as number) ?? 0
          const isBillable = record.get('billable') as boolean
          const activityType = record.get('activityType') as string

          totalHours += totalDuration
          if (isBillable) {
            billableHours += billedDuration
            nonBillableHours += Math.max(0, totalDuration - billedDuration)
          } else {
            nonBillableHours += totalDuration
          }

          if (activityType === ActivityType.SUPPORT) {
            supportHours += totalDuration
          }
          if (activityType === ActivityType.MANAGED_TEAM) {
            managedTeamHours += totalDuration
          }
        }

        const notTrackedHours = Math.max(0, totalWorkingTime - totalHours)

        return {
          userId,
          name,
          trackedHours: totalHours,
          billableHours,
          nonBillableHours,
          supportHours,
          managedTeamHours,
          workingHours: totalWorkingTime,
          utilizationWeighted: totalUtilizationWeighted,
          utilizationPct,
          absentDays,
          notTrackedHours,
        }
      }),
    )

    // 3. Totals
    const totals = {
      trackedHours: memberData.reduce((sum, m) => sum + m.trackedHours, 0),
      billableHours: memberData.reduce((sum, m) => sum + m.billableHours, 0),
      nonBillableHours: memberData.reduce(
        (sum, m) => sum + m.nonBillableHours,
        0,
      ),
      workingHours: memberData.reduce((sum, m) => sum + m.workingHours, 0),
      avgUtilizationPct: (() => {
        const weighted = memberData.reduce(
          (sum, m) => sum + m.utilizationWeighted,
          0,
        )
        const working = memberData.reduce((sum, m) => sum + m.workingHours, 0)
        return working > 0 ? Math.round((weighted * 100) / working) : 0
      })(),
    }

    return {
      period: { start: startDate, end: endDate },
      leaders: leaderData,
      members: memberData,
      totals,
      _instruction:
        'Present a utilization summary. Highlight: members with utilizationPct < 50, notTrackedHours > 0, high nonBillableHours. Group by leader if multiple leaders. Use a table or bullet list for clarity.',
    }
  },
})
